import { DATA_VERSION } from "./version";
import { shardOf, stripAccents } from "./keys";
import type { CoreData, CountryId, InsightsData, NameDetailData, NameEntry, RareData, TopData } from "./types";
import { decodeCore, type RawCore } from "./decode";

export { decodeCore };

/** relative, so the app works at a site root or inside a sub-path (the preview build) */
export const DATA_BASE = __PREVIEW__ ? "data/current" : `data/${DATA_VERSION}`;

/** the country whose data the shard, rare-name and top-list loaders read */
let base = `${DATA_BASE}/uk`;

/** Called when this copy of the app asks for data the site no longer has: a newer version is live. */
export const dataHooks: { outdated?: () => void } = {};

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    // each release's data has its own folder, so a page left open across an update asks for old files
    if (res.status === 404 && !__PREVIEW__ && !__NATIVE__) dataHooks.outdated?.();
    throw new Error(`${res.status} loading ${url}`);
  }
  return res.json() as Promise<T>;
}

/** A minimal entry for a recorded name that isn't in the swipe deck (too rare). */
export function rareEntry(key: string, name: string): NameEntry {
  const letters = stripAccents(name).replace(/[^A-Za-z]/g, "");
  return {
    idx: -1,
    name,
    key,
    g: "B",
    fs: 50,
    r: [0, 0],
    c: [0, 0],
    r5: [0, 0],
    t: [null, null],
    s: ["", ""],
    peakYear: 0,
    peakCount: 0,
    peakRank: 0,
    firstYear: 0,
    tags: 0,
    syl: 0,
    origin: null,
    meaning: "",
    originNote: "",
    pron: "",
    msrc: "",
    len: letters.length,
    initial: letters.charAt(0).toUpperCase(),
  };
}

function titleCase(key: string): string {
  return key.replace(/(^|[-' ])([a-z])/g, (_, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** Look a decided name up in the deck; rare names fall back to the saved (or title-cased) name. */
export function entryFor(core: CoreData, key: string, savedName?: string): NameEntry {
  return core.byKey.get(key) ?? rareEntry(key, savedName ?? titleCase(key));
}

async function fetchCore(country: CountryId): Promise<CoreData> {
  // index.html starts fetching the saved country's core before the app script runs
  const early = (window as { __nlCore?: { country: string; res: Promise<Response> } }).__nlCore;
  delete (window as { __nlCore?: unknown }).__nlCore;
  if (early && early.country === country) {
    try {
      const res = await early.res;
      if (res.ok) return decodeCore((await res.json()) as RawCore);
    } catch {
      /* fall through to a fresh request */
    }
  }
  return decodeCore(await getJson<RawCore>(`${DATA_BASE}/${country}/core.json`));
}

/** Load a country's names. Call showCountry() if they're the ones the app goes on to show. */
export async function loadCore(country: CountryId): Promise<CoreData> {
  return fetchCore(country);
}

/**
 * Point the other loaders (name details, top lists, rare names, trends) at a country: only once
 * its names are the ones shown, so a slower load the app has stopped waiting for can't move them.
 */
export function showCountry(country: CountryId): void {
  const next = `${DATA_BASE}/${country}`;
  if (next === base) return;
  base = next;
  shardCache.clear();
  groupCache.clear();
  rarePromise = null;
  topPromise = null;
  insightsPromise = null;
}

const shardCache = new Map<string, Promise<Record<string, NameDetailData>>>();
const groupCache = new Map<string, Promise<Record<string, Record<string, NameDetailData>>>>();

/** The preview build merges the detail shards four to one (00-03 in g/00.json, and so on). */
function loadGroup(id: string): Promise<Record<string, Record<string, NameDetailData>>> {
  const gid = (parseInt(id, 16) >> 2).toString(16).padStart(2, "0");
  let p = groupCache.get(gid);
  if (!p) {
    p = getJson<Record<string, Record<string, NameDetailData>>>(`${base}/g/${gid}.json`);
    p.catch(() => groupCache.delete(gid));
    groupCache.set(gid, p);
  }
  return p;
}

export function loadShard(id: string): Promise<Record<string, NameDetailData>> {
  let p = shardCache.get(id);
  if (!p) {
    p = __PREVIEW__
      ? loadGroup(id).then((g) => g[id] ?? {})
      : getJson<Record<string, NameDetailData>>(`${base}/d/${id}.json`);
    p.catch(() => shardCache.delete(id));
    shardCache.set(id, p);
  }
  return p;
}

export async function loadDetail(key: string): Promise<NameDetailData | null> {
  const shard = await loadShard(shardOf(key));
  return shard[key] ?? null;
}

/** Warm the shard cache for names about to be shown. */
export function prefetchDetails(keys: string[]): void {
  for (const id of new Set(keys.map(shardOf))) loadShard(id).catch(() => {});
}

let rarePromise: Promise<RareData> | null = null;

export function loadRare(): Promise<RareData> {
  if (!rarePromise) {
    rarePromise = getJson<RareData>(`${base}/rare.json`);
    rarePromise.catch(() => (rarePromise = null));
  }
  return rarePromise;
}

let topPromise: Promise<TopData> | null = null;

/** The top 100 for every year. */
export function loadTop(): Promise<TopData> {
  if (!topPromise) {
    topPromise = getJson<TopData>(`${base}/top.json`);
    topPromise.catch(() => (topPromise = null));
  }
  return topPromise;
}

let insightsPromise: Promise<InsightsData> | null = null;

/** The stories in the records: spellings counted together, movers, endings and more. */
export function loadInsights(): Promise<InsightsData> {
  if (!insightsPromise) {
    insightsPromise = getJson<InsightsData>(`${base}/insights.json`);
    insightsPromise.catch(() => (insightsPromise = null));
  }
  return insightsPromise;
}
